import { useContext, useMemo } from 'react';
import { deriveEntitlementCaps, type EntitlementCaps } from '@heybesafe/shared';
import type { ActivePromoCode, EntitlementSnapshot, EntitlementState } from '@heybesafe/shared';
import { SafeContext } from '../context/SafeContext';

export interface UseEntitlementResult extends EntitlementCaps {
  snapshot: EntitlementSnapshot | null;
  state: EntitlementState | null;
  trialDaysLeft: number | null;
  activePromoCode: ActivePromoCode | null;
  refresh: () => Promise<void>;
}

function computeTrialDaysLeft(snapshot: EntitlementSnapshot | null): number | null {
  if (!snapshot || snapshot.state !== 'trial' || !snapshot.trialEndsAt) return null;
  const endMs = new Date(snapshot.trialEndsAt).getTime();
  if (Number.isNaN(endMs)) return null;
  const diffMs = endMs - Date.now();
  return Math.max(0, Math.ceil(diffMs / 86_400_000));
}

/**
 * Read entitlement state from SafeContext + derive the cap booleans the UI
 * gates on. Returns a stable shape: components consume the boolean(s) they
 * care about and re-render when the underlying snapshot changes.
 */
export function useEntitlement(): UseEntitlementResult {
  const ctx = useContext(SafeContext);
  if (!ctx) {
    throw new Error('useEntitlement must be used inside SafeContext.Provider');
  }
  const { entitlement, refreshEntitlement, activePromoCode } = ctx;

  const caps = useMemo(() => deriveEntitlementCaps(entitlement), [entitlement]);

  return {
    ...caps,
    snapshot: entitlement,
    state: entitlement?.state ?? null,
    trialDaysLeft: computeTrialDaysLeft(entitlement),
    activePromoCode: activePromoCode ?? null,
    refresh: refreshEntitlement,
  };
}
